/**
 * Prop system — pure logic (no Phaser imports).
 * Handles generic destructible environment props (phone boxes, bins, crates, etc.).
 * Phaser rendering and scene wiring lives in StageScene.
 */
import type { PropDef } from '@/data/props';
import type { WeaponDef } from '@/data/weapons';
import { WORLD_WEAPONS } from '@/data/weapons';

export interface PropState {
  readonly def: PropDef;
  health: number;
  destroyed: boolean;
}

export type PropDrop = { kind: 'weapon'; def: WeaponDef } | { kind: 'food'; heal: number } | null;

/** Create a fresh prop state from a definition. */
export function createPropState(def: PropDef): PropState {
  return { def, health: def.health, destroyed: false };
}

/**
 * Apply one hit to a prop.
 * Returns true the moment the prop transitions to destroyed; false otherwise.
 * No-ops (returns false) if already destroyed.
 */
export function damageProp(prop: PropState, amount = 1): boolean {
  if (prop.destroyed) return false;
  prop.health = Math.max(0, prop.health - amount);
  if (prop.health <= 0) {
    prop.destroyed = true;
    return true;
  }
  return false;
}

/**
 * Resolve what a prop drops on destruction.
 * Returns null if the weapon id is unrecognised, the drop kind is 'none', or unknown.
 */
export function resolvePropDrop(def: PropDef): PropDrop {
  if (def.dropKind === 'weapon') {
    const weapon = WORLD_WEAPONS.find((w) => w.id === def.dropWeaponId);
    return weapon ? { kind: 'weapon', def: weapon } : null;
  }
  if (def.dropKind === 'food') {
    return { kind: 'food', heal: def.dropHeal ?? 0 };
  }
  return null;
}
